import { EmptyState } from '@/components/v1/molecules/empty-state/empty-state';
import {
  ObservabilitySourceAlert,
  observabilitySourceCurrent,
  observabilitySourceUnavailable,
} from '@/components/v1/observability/health';
import { MetricPanel } from '@/components/v1/observability/metric-panel';
import { Alert, AlertDescription, AlertTitle } from '@/components/v1/ui/alert';
import { Badge } from '@/components/v1/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/v1/ui/card';
import { Loading } from '@/components/v1/ui/loading';
import { useRefetchInterval } from '@/contexts/refetch-interval-context';
import {
  InfrastructureResource,
  ObservabilityRange,
  getObservabilityRefetchInterval,
  infrastructureResourceDetailQuery,
} from '@/lib/api/observability';
import { relativeDate } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';

const metricPanels = [
  { unit: 'cores', title: 'CPU', description: 'CPU usage' },
  { unit: 'bytes', title: 'Memory', description: 'Memory usage' },
  { unit: 'ratio', title: 'Storage', description: 'Storage utilization' },
  {
    unit: 'count',
    title: 'Resource health',
    description: 'Readiness and restarts',
  },
  {
    unit: 'milliseconds',
    title: 'Latency',
    description: 'Request latency',
  },
] as const;

export function InfrastructureResourceDetail(props: {
  kind: InfrastructureResource['kind'];
  uid: string;
  range: ObservabilityRange;
  customFrom?: string;
  customTo?: string;
}) {
  const { refetchInterval } = useRefetchInterval();
  const polling = getObservabilityRefetchInterval(refetchInterval);
  const detail = useQuery({
    ...infrastructureResourceDetailQuery(
      props.kind,
      props.uid,
      props.range,
      props.customFrom,
      props.customTo,
    ),
    refetchInterval: polling,
  });

  if (detail.isPending) {
    return <Loading />;
  }

  if (detail.isError && !detail.data) {
    return (
      <EmptyState
        title="Resource unavailable"
        description="The resource may have been replaced or telemetry is unavailable."
      />
    );
  }

  const resource = detail.data.data.resource;
  const metricsUnavailable = observabilitySourceUnavailable(
    detail.data.meta.sources,
    'prometheus',
  );
  const kubernetesUnavailable = observabilitySourceUnavailable(
    detail.data.meta.sources,
    'kubernetes',
  );
  const kubernetesNotCurrent =
    detail.isError ||
    !observabilitySourceCurrent(detail.data.meta.sources, 'kubernetes');
  const visibleMetricPanels = metricPanels
    .map((panel) => ({
      ...panel,
      series: detail.data.data.metrics.filter(
        (series) => series.unit === panel.unit,
      ),
    }))
    .filter((panel) => panel.series.length > 0);

  return (
    <div className="space-y-4 overflow-y-auto">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="break-all text-xl font-semibold">{resource.name}</h2>
          <Badge variant="outline">{resource.kind}</Badge>
          <Badge variant="outline">
            {kubernetesNotCurrent ? 'Unknown' : resource.status}
          </Badge>
        </div>
        {'namespace' in resource && (
          <p className="mt-1 text-sm text-muted-foreground">
            {resource.namespace}
          </p>
        )}
      </div>

      <ObservabilitySourceAlert
        partial={detail.data.meta.partial}
        sources={detail.data.meta.sources}
      />

      {props.range === 'custom' && (
        <Alert>
          <Activity />
          <AlertTitle>
            Historical metrics with current resource state
          </AlertTitle>
          <AlertDescription>
            Usage charts end at the selected time. Status, conditions, related
            resources, and Kubernetes events are current best-effort snapshots.
          </AlertDescription>
        </Alert>
      )}

      {visibleMetricPanels.length === 0 ? (
        <MetricPanel
          title="Resource usage"
          description="Metrics for the selected time range"
          series={[]}
          unavailable={metricsUnavailable}
        />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {visibleMetricPanels.map((panel) => (
            <MetricPanel
              key={panel.unit}
              title={panel.title}
              description={`${panel.description} over the selected time range`}
              series={panel.series}
              unavailable={metricsUnavailable}
            />
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Conditions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {kubernetesUnavailable ? (
            <EmptyState
              title="Conditions unavailable"
              description="Current Kubernetes conditions could not be checked."
            />
          ) : detail.data.data.conditions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No reported conditions.
            </p>
          ) : (
            detail.data.data.conditions.map((condition) => (
              <div key={condition.type} className="rounded-md border p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">{condition.type}</p>
                  <Badge variant="outline">{condition.status}</Badge>
                </div>
                {(condition.reason || condition.message) && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {[condition.reason, condition.message]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent events</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {kubernetesUnavailable ? (
            <EmptyState
              title="Events unavailable"
              description="The current Kubernetes event buffer could not be checked."
            />
          ) : detail.data.data.events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No recent events.</p>
          ) : (
            detail.data.data.events.slice(0, 20).map((event) => (
              <div key={event.id} className="rounded-md border p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium">{event.reason}</p>
                  <Badge
                    variant={
                      event.type === 'Warning' ? 'inProgress' : 'outline'
                    }
                  >
                    {event.type}
                  </Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {event.message}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {relativeDate(event.at)}
                </p>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Related resources</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {kubernetesUnavailable ? (
            <EmptyState
              title="Related resources unavailable"
              description="Current Kubernetes relationships could not be checked."
            />
          ) : detail.data.data.related.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No related resources were reported.
            </p>
          ) : (
            detail.data.data.related.slice(0, 20).map((related) => (
              <div
                key={related.uid}
                className="flex items-center justify-between gap-3 rounded-md border p-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">{related.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {'namespace' in related
                      ? `${related.kind} · ${related.namespace}`
                      : related.kind}
                  </p>
                </div>
                <Badge variant="outline">{related.status}</Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
